import React, { useState, useEffect } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  Pagination,
  Card,
  Tag,
  Space,
  message,
  Row,
  Col,
  Image
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined
} from "@ant-design/icons";
import axios from "axios";

const { TextArea } = Input;
const { Option } = Select;

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newProduct, setNewProduct] = useState({
    description: "",
    type: "",
    size: "",
    price: "",
    quantity: "",
    image: null,
    category_id: "",
  });
  const [updatedProduct, setUpdatedProduct] = useState({ ...newProduct });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [editFileList, setEditFileList] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [currentPage, pageSize]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get("https://agpipefitting.org.et/api/categories");
      setCategories(response.data);
    } catch (error) {
      message.error("Failed to load categories");
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://agpipefitting.org.et/api/product_descriptions", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          Accept: "application/json",
        },
        params: {
          page: currentPage,
          per_page: pageSize
        }
      });

      // Adjust based on your API response structure
      const data = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      const totalItems = response.data.total || response.data.meta?.total || data.length;
      
      setProducts(data);
      setTotal(totalItems);
    } catch (err) {
      console.error("Fetch products failed:", err);
      message.error("Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

const handleAddProduct = async (values) => {
  const formData = new FormData();
  formData.append("description", values.description || '');
  formData.append("type", values.type);
  formData.append("size", values.size);
  formData.append("category_id", values.category_id);

  // Optional fields
  if (values.price !== undefined && values.price !== null && values.price !== '') {
    formData.append("price", values.price);
  }
  if (values.quantity !== undefined && values.quantity !== null && values.quantity !== '') {
    formData.append("quantity", values.quantity);
  }

  if (values.image && values.image.length > 0) {
    formData.append("image", values.image[0].originFileObj);
  }

  try {
    setLoading(true);
    await axios.post(
      "https://agpipefitting.org.et/api/product_descriptions",
      formData,
      { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } }
    );

    message.success("Product added successfully");
    fetchProducts();
    setShowAddModal(false);
    form.resetFields();
    setFileList([]);
  } catch (error) {
    console.error("Error adding product:", error.response?.data || error.message);
    if (error.response?.data?.errors) {
      Object.entries(error.response.data.errors).forEach(([field, messages]) => {
        message.error(`${field}: ${messages.join(", ")}`);
      });
    } else {
      message.error("Failed to add product");
    }
  } finally {
    setLoading(false);
  }
};



  const handleEditProduct = async (values) => {
    const formData = new FormData();
    formData.append("description", values.description);
    formData.append("type", values.type);
    formData.append("size", values.size);
    formData.append("price", values.price);
    formData.append("quantity", values.quantity);
    formData.append("category_id", values.category_id);
    
    // Only append image if a new one is uploaded
    if (values.image && values.image.fileList && values.image.fileList.length > 0) {
      formData.append("image", values.image.fileList[0].originFileObj);
    }

    try {
      setLoading(true);
      await axios.post(`https://agpipefitting.org.et/api/product_descriptions/${currentProductId}?_method=PUT`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        }
      });
      
      message.success("Product updated successfully");
      fetchProducts();
      setShowEditModal(false);
      editForm.resetFields();
      setEditFileList([]);
    } catch (error) {
      console.error("Error updating product:", error);
      message.error("Failed to update product");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this product?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(`https://agpipefitting.org.et/api/product_descriptions/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            }
          });
          message.success("Product deleted successfully");
          fetchProducts();
        } catch (error) {
          console.error("Error deleting product:", error);
          message.error("Failed to delete product");
        }
      }
    });
  };

  const handleShowEditModal = (product) => {
    setCurrentProductId(product.id);
    editForm.setFieldsValue({
      description: product.description,
      type: product.type,
      size: product.size,
      price: product.price,
      quantity: product.quantity,
      category_id: product.category_id,
    });
    setUpdatedProduct({
      description: product.description,
      type: product.type,
      size: product.size,
      price: product.price,
      quantity: product.quantity,
      image: product.image,
      category_id: product.category_id,
    });
    setShowEditModal(true);
  };

  const columns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
      width: 60,
    },
    {
      title: "Product",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: 100,
      render: (price) => `$${parseFloat(price).toFixed(2)}`,
    },
    {
      title: "Stock",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (quantity) => (
        <Tag color={quantity > 10 ? "green" : quantity > 0 ? "orange" : "red"}>
          {quantity}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: ["category", "name"],
      key: "category",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleShowEditModal(record)}
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDeleteProduct(record.id)}
          />
        </Space>
      ),
    },
  ];

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
      }
      return isImage || Upload.LIST_IGNORE;
    },
    maxCount: 1,
    fileList: fileList,
    onChange: ({ fileList }) => {
      setFileList(fileList);
    },
  };

  const editUploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
      }
      return isImage || Upload.LIST_IGNORE;
    },
    maxCount: 1,
    fileList: editFileList,
    onChange: ({ fileList }) => {
      setEditFileList(fileList);
    },
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title="Products Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddModal(true)}
          >
            Add New Product
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* Add Product Modal */}
      <Modal
        title="Add New Product"
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddProduct}
          initialValues={newProduct}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Product Type"
                name="type"
                rules={[{ required: true, message: "Please enter product type" }]}
              >
                <Input placeholder="e.g. T-shirt" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Product Size"
                name="size"
                rules={[{ required: true, message: "Please enter product size" }]}
              >
                <Input placeholder="e.g. M, L, XL" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Price"
                name="price"
              >
                <InputNumber
                  placeholder="e.g. 49.99"
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Stock Quantity"
                name="quantity"
              >
                <InputNumber
                  placeholder="e.g. 100"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Category"
                name="category_id"
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <Select placeholder="Select a category">
                  {categories.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Product Image"
                name="image"
                rules={[{ required: true, message: "Please upload an image" }]}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Product Description"
            name="description"
            rules={[{ required: true, message: "Please enter product description" }]}
          >
            <TextArea
              rows={4}
              placeholder="Provide a detailed and inclusive product description (materials, usage, highlights, etc.)"
            />
          </Form.Item>

          <Form.Item style={{ textAlign: "right" }}>
            <Button onClick={() => setShowAddModal(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Add Product
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        title="Edit Product"
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditProduct}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Product Type"
                name="type"
                rules={[{ required: true, message: "Please enter product type" }]}
              >
                <Input placeholder="e.g. T-shirt" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Product Size"
                name="size"
                rules={[{ required: true, message: "Please enter product size" }]}
              >
                <Input placeholder="e.g. M, L, XL" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Price"
                name="price"
              >
                <InputNumber
                  placeholder="e.g. 49.99"
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Stock Quantity"
                name="quantity"
              >
                <InputNumber
                  placeholder="e.g. 100"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Category"
                name="category_id"
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <Select placeholder="Select a category">
                  {categories.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Product Image"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload {...editUploadProps}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Product Description"
            name="description"
            rules={[{ required: true, message: "Please enter product description" }]}
          >
            <TextArea
              rows={4}
              placeholder="Provide a detailed and inclusive product description (materials, usage, highlights, etc.)"
            />
          </Form.Item>

          {updatedProduct.image && typeof updatedProduct.image === 'string' && (
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 8 }}>Current Image:</span>
              <Image
                width={100}
                src={`https://agpipefitting.org.et/storage/${updatedProduct.image}`}
                fallback="https://via.placeholder.com/100"
              />
            </div>
          )}

          <Form.Item style={{ textAlign: "right" }}>
            <Button onClick={() => setShowEditModal(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Product
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ProductsPage;